import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import './SwaggerOperationRenderer.css';
import { generateCurlSnippet, generateFetchSnippet } from '../../utils/codeSamples';

/**
 * SwaggerOperationRenderer - A reusable component for rendering Swagger/OpenAPI operation details
 * Displays HEADERS, REQUEST BODY, and RESPONSES in a structured format
 */
const SwaggerOperationRenderer = ({ 
  operation, 
  definitions = {}, 
  path = '',
  method = '',
  showSummary = true,
  expandedByDefault = {},
  swaggerData
}) => {
  const [expandedSchemas, setExpandedSchemas] = useState(expandedByDefault);
  const [codeTab, setCodeTab] = useState('curl');

  // Helper function to toggle schema expansion
  const toggleSchemaExpansion = (schemaKey) => {
    setExpandedSchemas(prev => ({
      ...prev,
      [schemaKey]: !prev[schemaKey]
    }));
  };

  // Helper function to render schema properties recursively
  const renderSchemaProperties = (schema, schemaName, parentKey = '') => {
    const currentKey = parentKey ? `${parentKey}.${schemaName}` : schemaName;
    const isExpanded = expandedSchemas[currentKey];
    
    if (!schema) return null;
    
    // Handle references
    if (schema.$ref) {
      const refName = schema.$ref.split('/').pop();
      const refSchema = definitions[refName];
      
      return (
        <div className="schema-ref-item">
          {refSchema && (
            <div className="schema-properties-expanded no-header">
              {refSchema.properties ? (
                // Show the properties directly without the type name header or description
                <div className="schema-properties-list">
                  {Object.entries(refSchema.properties).map(([propName, propSchema]) => {
                    // Check if this property is a reference or complex type that should be expandable
                    const isExpandable = propSchema.$ref || propSchema.type === 'object' || propSchema.type === 'array';
                    const propKey = `${currentKey}.${propName}`;
                    const isPropExpanded = expandedSchemas[propKey];
                    
                    // Get the type name for display
                    let typeName = propSchema.type || 'any';
                    let enumValues = null;
                    
                    if (propSchema.$ref) {
                      typeName = propSchema.$ref.split('/').pop();
                      // Check if the referenced schema has enum values
                      const refName = propSchema.$ref.split('/').pop();
                      const refSchema = definitions[refName];
                      if (refSchema?.enum) {
                        enumValues = refSchema.enum;
                      }
                    } else if (propSchema.type === 'array' && propSchema.items?.$ref) {
                      typeName = `Array of ${propSchema.items.$ref.split('/').pop()}`;
                    } else if (propSchema.enum) {
                      // Direct enum on the property
                      enumValues = propSchema.enum;
                    }
                    
                    return (
                      <div key={propName} className="property-row-container">
                        <div className="property-row">
                          <div className="property-name-wrapper">
                            {isExpandable && (
                              <span 
                                className="property-expand-arrow"
                                onClick={(e) => { e.stopPropagation(); toggleSchemaExpansion(propKey); }}
                              >
                                {isPropExpanded ? '▼' : '▶'}
                              </span>
                            )}
                            <span 
                              className={`property-name ${isExpandable ? 'expandable' : ''}`}
                              onClick={isExpandable ? (e) => { e.stopPropagation(); toggleSchemaExpansion(propKey); } : undefined}
                            >
                              {propName}
                            </span>
                          </div>
                          <div className="property-type">
                            {typeName}
                            {propSchema.format && ` (${propSchema.format})`}
                          </div>
                          <div className={refSchema.required?.includes(propName) ? 'property-required' : 'property-optional'}>
                            {refSchema.required?.includes(propName) ? 'required' : 'optional'}
                          </div>
                        </div>
                        {propSchema.description && (
                          <div className="property-description-full">
                            {propSchema.description}
                          </div>
                        )}
                        {isExpandable && isPropExpanded && (
                          <div className={
                            propSchema.type === 'array' || 
                            (propSchema.items && (propSchema.items.$ref || propSchema.items.type))
                              ? "nested-schema" 
                              : "nested-schema-for-simple-objects"
                          }>
                            {propSchema.$ref && enumValues && enumValues.length > 0 ? (
                              // For refs with enums, show the enum values as simple content
                              <div className="schema-simple-container">
                                <div className="schema-simple-type">
                                  <span className="simple-type">string</span>
                                  <span className="enum-values"> (Enum: {enumValues.join(', ')})</span>
                                </div>
                                {propSchema.description && (
                                  <div className="simple-description">{propSchema.description}</div>
                                )}
                              </div>
                            ) : (
                              renderSchemaProperties(propSchema, propName, currentKey)
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                renderSchemaProperties(refSchema, refName, currentKey)
              )}
            </div>
          )}
        </div>
      );
    }
    
    // Handle arrays
    if (schema.type === 'array' && schema.items) {
      return (
        <div className="schema-array-item">
          <div className="array-items">
            {renderSchemaProperties(schema.items, 'items', currentKey)}
          </div>
        </div>
      );
    }
    
    // Handle objects with properties
    if (schema.properties) {
      return (
        <div className="schema-properties-list">
          {Object.entries(schema.properties).map(([propName, propSchema]) => {
            // Check if this property is a reference or complex type that should be expandable
            const isExpandable = propSchema.$ref || propSchema.type === 'object' || propSchema.type === 'array';
            const propKey = `${currentKey}.${propName}`;
            const isPropExpanded = expandedSchemas[propKey];
            
            // Get the type name for display
            let typeName = propSchema.type || 'any';
            let enumValues = null;
            
            if (propSchema.$ref) {
              typeName = propSchema.$ref.split('/').pop();
              // Check if the referenced schema has enum values
              const refName = propSchema.$ref.split('/').pop();
              const refSchema = definitions[refName];
              if (refSchema?.enum) {
                enumValues = refSchema.enum;
              }
            } else if (propSchema.type === 'array' && propSchema.items?.$ref) {
              typeName = `Array of ${propSchema.items.$ref.split('/').pop()}`;
            } else if (propSchema.enum) {
              // Direct enum on the property
              enumValues = propSchema.enum;
            }
            
            return (
              <div key={propName} className="property-row-container">
                <div className="property-row">
                  <div className="property-name-wrapper">
                    {isExpandable && (
                      <span 
                        className="property-expand-arrow"
                        onClick={() => toggleSchemaExpansion(propKey)}
                      >
                        {isPropExpanded ? '▼' : '▶'}
                      </span>
                    )}
                    <span 
                      className={`property-name ${isExpandable ? 'expandable' : ''}`}
                      onClick={isExpandable ? () => toggleSchemaExpansion(propKey) : undefined}
                    >
                      {propName}
                    </span>
                  </div>
                  <div className="property-type">
                    {typeName}
                    {propSchema.format && ` (${propSchema.format})`}
                  </div>
                  <div className={schema.required?.includes(propName) ? 'property-required' : 'property-optional'}>
                    {schema.required?.includes(propName) ? 'required' : 'optional'}
                  </div>
                </div>
                {propSchema.description && (
                  <div className="property-description-full">
                    {propSchema.description}
                  </div>
                )}
                {isExpandable && isPropExpanded && (
                  <div className={
                    propSchema.type === 'array' || 
                    (propSchema.items && (propSchema.items.$ref || propSchema.items.type))
                      ? "nested-schema" 
                      : "nested-schema-for-simple-objects"
                  }>
                    {propSchema.$ref && enumValues && enumValues.length > 0 ? (
                      // For refs with enums, show the enum values as simple content
                      <div className="schema-simple-container">
                        <div className="schema-simple-type">
                          <span className="simple-type">string</span>
                          <span className="enum-values"> (Enum: {enumValues.join(', ')})</span>
                        </div>
                        {propSchema.description && (
                          <div className="simple-description">{propSchema.description}</div>
                        )}
                      </div>
                    ) : (
                      renderSchemaProperties(propSchema, propName, currentKey)
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    
    // Handle simple types
    return (
      <div className="schema-simple-container">
        <div className="schema-simple-type">
          <span className="simple-type">{schema.type || 'any'}</span>
          {schema.format && <span className="simple-format">({schema.format})</span>}
          {schema.example && <span className="simple-example">Example: {JSON.stringify(schema.example)}</span>}
        </div>
        {schema.description && (
          <div className="simple-description">{schema.description}</div>
        )}
      </div>
    );
  };

  if (!operation) {
    return <div className="swagger-operation-renderer-error">No operation data provided</div>;
  }

  return (
    <div className="swagger-operation-renderer">
      {/* Operation Summary */}
      {showSummary && (operation.summary || operation.description) && (
        <div className="operation-summary">
          {operation.summary && (
            <p className="operation-summary-text">
              <strong>Summary:</strong> {operation.summary}
            </p>
          )}
          {operation.description && (
            <div className="operation-description-text">
              <strong>Description:</strong>
              <div className="operation-description-content">
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                  {operation.description}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Path Parameters Section */}
      {operation.parameters?.filter(p => p.in === 'path').length > 0 && (
        <div className="operation-section">
          <h4 className="section-title">PATH PARAMETERS</h4>
          <div className="parameters-structured">
            {operation.parameters
              .filter(p => p.in === 'path')
              .map((param, idx) => (
                <div key={idx} className="parameter-item">
                  <div className="param-header">
                    <span className="param-name">{param.name}</span>
                    <span className="param-required">required</span>
                  </div>
                  <div className="param-details">
                    <div className="param-type">
                      {param.schema?.type || param.type || 'string'}
                      {param.schema?.format && ` (${param.schema.format})`}
                      {param.example && (
                        <>
                          <br />
                          <span className="param-example">Example: {param.example}</span>
                        </>
                      )}
                    </div>
                    {param.description && (
                      <div className="param-description">{param.description}</div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Headers Section */}
      {operation.parameters?.filter(p => p.in === 'header').length > 0 && (
        <div className="operation-section">
          <h4 className="section-title">HEADER PARAMETERS</h4>
          <div className="parameters-structured">
            {operation.parameters
              .filter(p => p.in === 'header')
              .map((param, idx) => (
                <div key={idx} className="parameter-item">
                  <div className="param-header">
                    <span className="param-name">{param.name}</span>
                    {param.required && <span className="param-required">required</span>}
                  </div>
                  <div className="param-details">
                    <div className="param-type">
                      {param.schema?.type || param.type || 'string'}
                      {param.schema?.format && ` (${param.schema.format})`}
                      {param.example && (
                        <>
                          <br />
                          <span className="param-example">Example: {param.example}</span>
                        </>
                      )}
                    </div>
                    {param.description && (
                      <div className="param-description">{param.description}</div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Query Parameters Section */}
      {operation.parameters?.filter(p => p.in === 'query').length > 0 && (
        <div className="operation-section">
          <h4 className="section-title">QUERY PARAMETERS</h4>
          <div className="parameters-structured">
            {operation.parameters
              .filter(p => p.in === 'query')
              .map((param, idx) => (
                <div key={idx} className="parameter-item">
                  <div className="param-header">
                    <span className="param-name">{param.name}</span>
                    {param.required && <span className="param-required">required</span>}
                  </div>
                  <div className="param-details">
                    <div className="param-type">
                      {param.type || 'string'}
                      {param.format && ` (${param.format})`}
                      {param.example && (
                        <>
                          <br />
                          <span className="param-example">Example: {param.example}</span>
                        </>
                      )}
                    </div>
                    {param.description && (
                      <div className="param-description">{param.description}</div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Request Body Section */}
      {operation.requestBody && (
        <div className="operation-section">
          <h4 className="section-title">REQUEST BODY</h4>
          <div className="request-body-structured">
            {operation.requestBody.description && (
              <p className="body-description">{operation.requestBody.description}</p>
            )}
            {operation.requestBody.required && (
              <span className="param-required">required</span>
            )}
            {operation.requestBody.content && 
              Object.entries(operation.requestBody.content).map(([contentType, content]) => (
                <div key={contentType} className="content-type-section">
                  <div className="content-type-header">{contentType}</div>
                  {content.schema && (
                    <div className="schema-section">
                      {renderSchemaProperties(content.schema, 'requestBody')}
                    </div>
                  )}
                </div>
              ))
            }
          </div>
        </div>
      )}

      {/* Responses Section */}
      {operation.responses && (
        <div className="operation-section">
          <h4 className="section-title">RESPONSES</h4>
          <div className="responses-structured">
            {Object.entries(operation.responses).map(([code, response]) => (
              <div key={code} className="response-item-structured">
                <div className="response-header">
                  <span className={`response-code status-${code[0]}xx`}>{code}</span>
                  <span className="response-description">{response.description}</span>
                </div>
                {response.content && 
                  Object.entries(response.content).map(([contentType, content]) => (
                    <div key={contentType} className="response-content-structured">
                      <div className="content-type-label">{contentType}</div>
                      {content.schema && (
                        <div className="response-schema">
                          <div className="schema-title">Response Schema:</div>
                          {renderSchemaProperties(content.schema, `response-${code}`)}
                        </div>
                      )}
                    </div>
                  ))
                }
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Code Samples */}
      <div className="operation-section">
        <h4 className="section-title">CODE SAMPLES</h4>
        <div className="code-tabs">
          {['curl', 'fetch'].map((tab) => (
            <button
              key={tab}
              type="button"
              className={`code-tab ${codeTab === tab ? 'active' : ''}`}
              onClick={() => setCodeTab(tab)}
            >
              {tab.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="code-sample">
          <pre>
            {codeTab === 'curl'
              ? generateCurlSnippet({
                  swagger: swaggerData,
                  method: method || operation.method,
                  path: path || operation.path,
                  parameters: operation.parameters || [],
                  requestBody: operation.requestBody,
                })
              : generateFetchSnippet({
                  swagger: swaggerData,
                  method: method || operation.method,
                  path: path || operation.path,
                  parameters: operation.parameters || [],
                  requestBody: operation.requestBody,
                })}
          </pre>
        </div>
      </div>
    </div>
  );
};

SwaggerOperationRenderer.propTypes = {
  operation: PropTypes.object.isRequired,
  definitions: PropTypes.object,
  path: PropTypes.string,
  method: PropTypes.string,
  showSummary: PropTypes.bool,
  expandedByDefault: PropTypes.object
};

export default SwaggerOperationRenderer;
